import {
  assign,
  chain,
  find,
  first,
  get,
  keyBy,
  map,
  sortBy,
  toLower,
  values,
} from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { MobileStepper, Step, StepLabel, Stepper } from '@mui/material';
import styles from './Deliveries.module.css';
import SelectVendor from './Steps/SelectVendor';
import BoxesAndUnits, {
  DEFAULT_UNITS_PER_BOX,
  getOrdersQuantity,
} from './Steps/BoxesAndUnits';
import SelectTimeSlot from './Steps/SelectTimeSlot';
import ConfirmDeliveryDetails from './Steps/ConfirmDeliveryDetails';
import PurchaseOrderSelector from './Steps/PurchaseOrderSelector/PurchaseOrderSelector';
import QuickOrderSearch from './QuickOrderSearch';
import LinearProgress from '../ReusableComponents/LinearProgress';
import StepConnector from '../ReusableComponents/StepConnector/StepConnector';
import WideModal from '../ReusableComponents/WideModal';
import BookingTypes from '../../Lib/Constants/booking-types';
import makeVendorOption from '../../Lib/Utils/Select/make-vendor-option';
import vendorProps from '../../Lib/Utils/PropTypes/vendor-props';
import addWeekDays from '../../Lib/Utils/Date/add-week-days';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext.tsx';

const makeDeliveryState = (
  delivery,
  locations = [],
  defaultLocation = null,
  vendor = null,
) => {
  const {
    id,
    boxes,
    units,
    location_id,
    starts_at,
    purchase_orders,
    vendor: deliveryVendor,
  } = delivery;

  const baseDefaultLocation = get(
    find(locations, ({ name }) => toLower(name) === 'rim'),
    'id',
    first(values(locations)).id,
  );

  // location_id -> editing a delivery
  // defaultLocation -> scheduler creating a booking for a location
  // defaultLocation -> set in the db per Vendor for when they book their own deliveries
  // baseDefaultLocation -> Vendors booking their own deliveries go to RIM unless a Vendor has a defaultLocation

  return {
    id,
    boxes,
    units,
    purchaseOrders: keyBy(purchase_orders, 'id') || {},
    vendor: vendor || deliveryVendor,
    location_id: location_id || get(defaultLocation, 'id', baseDefaultLocation),
    timeSlot: starts_at ? new Date(starts_at) : null,
  };
};

const makeSteps = (editing) =>
  [
    'Select A Vendor',
    'Select Purchase Orders For Delivery',
    'Boxes and Units',
    'Select Delivery Time',
    'Confirm Delivery Details',
  ].slice(editing ? 1 : 0);

const getDefaultTimeSelectorDate = (defaultDate, location) => {
  if (!location && !defaultDate) return moment.utc();
  if (defaultDate) return defaultDate;

  const startOfToday = moment
    .utc(moment().format('LLLL'), 'LLLL')
    .startOf('day');

  return addWeekDays(startOfToday, location.min_booking_days);
};

class DeliveryModal extends Component {
  constructor(props) {
    super(props);

    const { locations, defaultLocation, vendor } = props;

    this.state = {
      step: 0,
      width: window.innerWidth,
      delivery: {
        id: null,
        boxes: null,
        units: null,
        purchaseOrders: {},
        vendor,
        location_id: get(defaultLocation, 'id', first(values(locations)).id),
        timeSlot: null,
      },
    };

    this.handleWindowSizeChange = this.handleWindowSizeChange.bind(this);
    this.renderStep = this.renderStep.bind(this);
    this.saveDelivery = this.saveDelivery.bind(this);
    this.setVendor = this.setVendor.bind(this);
    this.setBoxesAndUnits = this.setBoxesAndUnits.bind(this);
    this.setTimeSlot = this.setTimeSlot.bind(this);
    this.setSelected = this.setSelected.bind(this);
    this.setLocation = this.setLocation.bind(this);
    this.next = this.next.bind(this);
    this.back = this.back.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onRendered = this.onRendered.bind(this);

    props.fetchVendors();
  }

  UNSAFE_componentWillMount() {
    window.addEventListener('resize', this.handleWindowSizeChange);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowSizeChange);
  }

  onClose(refreshData) {
    const { onClose, locations, defaultLocation } = this.props;
    const state = {
      step: 0,
      delivery: makeDeliveryState({}, locations, defaultLocation),
    };
    this.setState(state, () => onClose(refreshData));
  }

  onRendered() {
    const { delivery, locations, defaultLocation, vendor, vendorView } =
      this.props;
    const { step, delivery: d } = this.state;
    const selectedVendor = get(delivery, 'vendor', vendor);

    const mergeDelivery = assign(d, delivery);

    this.setState({
      step: step === 0 && (vendorView ? true : !!delivery.id) ? 1 : 0,
      delivery: makeDeliveryState(
        mergeDelivery,
        locations,
        defaultLocation,
        selectedVendor,
      ),
    });
  }

  setVendor(vendor) {
    const { delivery } = this.state;
    const state = { delivery: assign(delivery, { vendor }) };

    this.setState(state);
  }

  setBoxesAndUnits({ boxes, units }) {
    const { delivery } = this.state;
    const state = { delivery: assign(delivery, { boxes, units }) };

    this.setState(state);
  }

  setTimeSlot(timeSlot) {
    const { delivery } = this.state;
    const state = { delivery: assign(delivery, { timeSlot }) };

    this.setState(state);
  }

  setSelected(purchaseOrders) {
    const { delivery } = this.state;
    const { superuser } = this.props;

    const units = superuser ? getOrdersQuantity(purchaseOrders) : 0;
    const boxes = superuser ? Math.ceil(units / DEFAULT_UNITS_PER_BOX) : 0;
    const createMode =
      superuser && get(this.props.delivery, 'purchase_orders.length', 0) === 0;

    const state = {
      delivery: assign(delivery, {
        purchaseOrders,
        ...(createMode && {
          units,
          boxes,
        }),
      }),
    };

    this.setState(state);
  }

  setLocation(location) {
    if (!location) return;

    const { delivery } = this.state;
    const state = { delivery: assign(delivery, { location_id: location.id }) };

    this.setState(state);
  }

  next() {
    const { step } = this.state;

    this.setState({ step: step + 1 });
  }

  back() {
    const { step } = this.state;

    this.setState({ step: step - 1 });
  }

  saveDelivery() {
    const {
      delivery: { id, purchaseOrders, boxes, units, timeSlot, location_id },
    } = this.state;
    const purchaseOrderIdentifiers = map(purchaseOrders, 'po_number');
    const { saveDelivery, locations, defaultLocation } = this.props;
    const starts_at = timeSlot;
    const ends_at = moment(timeSlot).add(30, 'minutes').toDate();

    const delivery = {
      id,
      location_id,
      boxes,
      units,
      starts_at,
      ends_at,
      type: BookingTypes.ORDER_DELIVERY,
    };

    this.setState(
      { step: 0, delivery: makeDeliveryState({}, locations, defaultLocation) },
      () => saveDelivery(delivery, purchaseOrderIdentifiers),
    );
  }

  handleWindowSizeChange() {
    this.setState({ width: window.innerWidth });
  }

  renderStep(step) {
    const {
      availabilities,
      defaultDate,
      delivery: pDelivery,
      deliverablePurchaseOrders,
      deliveries,
      currentUserId,
      fetchVendors,
      getAvailabilities,
      getDeliveries,
      getDeliverablePurchaseOrders,
      loading,
      vendors,
      superuser,
      vendorView,
      locations,
    } = this.props;
    const { delivery } = this.state;
    const { vendor, purchaseOrders, location_id } = delivery;
    const deliverableOrders = chain(pDelivery.purchase_orders)
      .concat(sortBy(values(deliverablePurchaseOrders), 'due_date'))
      .compact()
      .uniqBy('id')
      .keyBy('id')
      .value();
    const deliveryLocation = get(locations, `${delivery.location_id}`);

    switch (step) {
      case 0:
        return (
          <SelectVendor
            vendors={vendors}
            next={this.next}
            superuser={superuser}
            vendor={makeVendorOption(vendor)}
            fetchVendors={fetchVendors}
            onChange={this.setVendor}
            onClose={this.onClose}
          />
        );
      case 1:
        return (
          <PurchaseOrderSelector
            back={this.back}
            next={this.next}
            delivery={delivery}
            editing={!!delivery.id}
            superuser={superuser}
            purchaseOrders={deliverableOrders}
            saveDelivery={this.saveDelivery}
            selected={purchaseOrders}
            setLocation={vendorView ? this.setLocation : () => {}}
            locations={locations}
            loading={loading}
            getPurchaseOrders={(locationId, params) => {
              getDeliverablePurchaseOrders(
                vendorView ? currentUserId : vendor.id,
                locationId || location_id,
                !vendorView,
                params,
              );
            }}
            onClose={this.onClose}
            onSelect={this.setSelected}
            vendorUser={first(get(vendor, 'contacts'))}
            vendorView={vendorView}
          />
        );
      case 2:
        return (
          <BoxesAndUnits
            back={this.back}
            delivery={delivery}
            locations={locations}
            next={this.next}
            onChange={this.setBoxesAndUnits}
            onClose={this.onClose}
            purchaseOrders={purchaseOrders}
            superuser={superuser}
          />
        );
      case 3:
        return (
          <SelectTimeSlot
            defaultDate={getDefaultTimeSelectorDate(
              defaultDate,
              deliveryLocation,
            )}
            deliveryDetails={delivery}
            deliveries={deliveries}
            getDeliveries={getDeliveries}
            onChange={this.setTimeSlot}
            onClose={this.onClose}
            getAvailabilities={getAvailabilities}
            back={this.back}
            availabilities={availabilities}
            next={this.next}
            loading={loading}
            location={get(locations, delivery.location_id)}
            vendorView={vendorView}
          />
        );
      case 4:
        return (
          <ConfirmDeliveryDetails
            back={this.back}
            delivery={delivery}
            loading={loading}
            locations={locations}
            onClose={this.onClose}
            onSaved={this.onClose}
            saveDelivery={this.saveDelivery}
            setLocation={vendorView ? null : this.setLocation}
          />
        );
      default:
        return <div />;
    }
  }

  render() {
    const { open, loading, delivery, vendorView } = this.props;
    const { step, width } = this.state;
    const isMobile = width <= 700;
    const steps = makeSteps(vendorView ? true : !!delivery.id);

    return (
      <WideModal
        open={open}
        onClickAway={() => {}}
        onClose={() => this.onClose(true)}
        onRendered={this.onRendered}
      >
        {step === 1 && !vendorView && <QuickOrderSearch />}

        <div className={styles.deliveryContainer}>
          <LinearProgress loading={loading} />
          <div className={styles.deliveryContent}>{this.renderStep(step)}</div>
          {isMobile ? (
            <MobileStepper
              variant="progress"
              position="static"
              activeStep={step}
              steps={steps.length}
            />
          ) : (
            <Stepper
              connector={<StepConnector />}
              className={styles.deliveryStepper}
              activeStep={vendorView ? step - 1 : step}
            >
              {steps.map((s, i) => {
                const completed = i < (vendorView ? step - 1 : step);

                return (
                  <Step key={s} completed={completed}>
                    <StepLabel>{s}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          )}
        </div>
      </WideModal>
    );
  }
}

DeliveryModal.propTypes = {
  availabilities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  defaultDate: PropTypes.instanceOf(moment),
  defaultLocation: PropTypes.shape({}),
  delivery: PropTypes.shape({}).isRequired,
  deliveries: PropTypes.shape({}),
  deliverablePurchaseOrders: PropTypes.shape({}).isRequired,
  currentUserId: PropTypes.string,
  fetchVendors: PropTypes.func,
  getAvailabilities: PropTypes.func.isRequired,
  getDeliveries: PropTypes.func,
  getDeliverablePurchaseOrders: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  locations: PropTypes.shape({}).isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  saveDelivery: PropTypes.func.isRequired,
  superuser: PropTypes.bool,
  vendors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  ),
  vendor: vendorProps,
  vendorView: PropTypes.bool,
};

DeliveryModal.contextType = FeatureFlagsContext;

DeliveryModal.defaultProps = {
  superuser: false,
  currentUserId: null,
  defaultDate: null,
  deliveries: {},
  fetchVendors: () => {},
  getDeliveries: () => {},
  defaultLocation: null,
  vendors: [],
  vendor: null,
  vendorView: false,
};

export default DeliveryModal;
